import { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { Color } from "react-color";
import { Form } from "react-bootstrap";
import { ColorPicker } from "../create";
import { parseColorToHex } from "utils/functions";

export type ColorStop = [number, Color, string?];

type ColorRangeBarProps = {
    min: number;
    max: number;
    colors: ColorStop[];
    draggable?: boolean;
    mode?: "default" | "compact";
    onChange?(newColors: ColorStop[]): void;
};
type OffsetConverter = {
    toPercent: (offset: number) => number;
    fromPercent: (offset: number) => number;
};
type ColorRangeEditorProps = {
    min: number;
    max: number;
    colors: ColorStop[];
    range: number;
    onRangeChange: (range: number) => void;
    onColorChange: (color: string, index: number) => void;
    onStopsChange?: (colors: ColorStop[]) => void;
    rangeOptions?: number[];
    className?: string;
    mode?: "default" | "compact";
};

const ColorRange = styled.div<{ mode: "default" | "compact" }>`
    position: relative;
    width: 100%;
    padding-top: ${({ mode }) => (mode === "compact" ? "12px" : "24px")};
    padding-bottom: ${({ mode }) => (mode === "compact" ? "12px" : "24px")};

    .color-range__bar {
        display: flex;
        height: ${({ mode }) => (mode === "compact" ? "10px" : "12px")};
        border-radius: 999px;
        overflow: hidden;
        background: #1f2933;
    }

    .color-range__segment {
        height: 100%;
    }

    .color-range__markers {
        position: absolute;
        left: 0;
        right: 0;
        top: 0;
        bottom: 0;
        pointer-events: none;
    }

    .color-range__marker {
        position: absolute;
        transform: translateX(-50%);
        top: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        background: transparent;
        border: 0;
        padding: 0;
    }

    .color-range__marker--draggable {
        pointer-events: auto;
        cursor: ew-resize;
    }

    .color-range__marker-arrow {
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-top: 8px solid #ffffff;
    }

    .color-range__marker-label {
        margin-top: 0;
        font-size: ${({ mode }) => (mode === "compact" ? "11px" : "12px")};
        line-height: 1;
        color: #9aa4c1;
        white-space: nowrap;
    }

    .color-range__handle {
        position: absolute;
        transform: translateX(-50%);
        bottom: 0;
        width: 24px;
        height: 20px;
        background: transparent;
        border: 0;
        padding: 0;
        cursor: ew-resize;
        pointer-events: auto;
        display: flex;
        align-items: flex-end;
        justify-content: center;
    }

    .color-range__handle::before {
        content: "";
        width: 0;
        height: 0;
        border-left: 6px solid transparent;
        border-right: 6px solid transparent;
        border-bottom: 8px solid #ffffff;
    }

    .color-range__drag-indicator {
        position: absolute;
        transform: translateX(-50%);
        top: 0;
        pointer-events: none;
    }

    .color-range__drag-label {
        margin-top: 0;
        font-size: ${({ mode }) => (mode === "compact" ? "11px" : "12px")};
        line-height: 1;
        color: #ffffff;
        white-space: nowrap;
        border: 1px solid #ffffff;
        border-radius: 4px;
        padding: 2px 6px;
        background: rgba(0, 0, 0, 0.35);
    }
`;

const clamp = (v: number, min: number, max: number) =>
    Math.min(max, Math.max(min, v));

const offsetToValue = (offset: number, min: number, max: number) =>
    min + ((max - min) * offset) / 100;

const formatMarkerValue = (offset: number, min: number, max: number) => {
    const rawValue = offsetToValue(offset, min, max);
    const range = max - min;
    return range <= 50 ? rawValue.toFixed(2) : String(Math.round(rawValue));
};

const getOffsetConverter = (usesDecimalOffset: boolean): OffsetConverter => {
    if (usesDecimalOffset) {
        return {
            toPercent: (offset: number) => clamp(offset * 100, 0, 100),
            fromPercent: (offset: number) => Number((offset / 100).toFixed(4)),
        };
    }

    return {
        toPercent: (offset: number) => clamp(offset, 0, 100),
        fromPercent: (offset: number) => Number(offset.toFixed(2)),
    };
};

function sortAndNormalizeStops(
    stops: ColorStop[],
    toPercent: OffsetConverter["toPercent"]
): ColorStop[] {
    return [...stops]
        .map(([offset, color, label]) => [
            toPercent(offset),
            color,
            label,
        ] as ColorStop)
        .sort((a, b) => a[0] - b[0]);
}

function buildSegments(stops: ColorStop[]) {
    if (!stops.length) return [];

    const sorted = [...stops].sort((a, b) => a[0] - b[0]);
    const segments: { width: number; color: Color }[] = [];
    let prevPct = 0;
    let lastColor = sorted[0][1];

    for (const [offset, color] of sorted) {
        const width = offset - prevPct;
        if (width > 0) {
            segments.push({ width, color });
        }
        prevPct = offset;
        lastColor = color;
    }

    if (prevPct < 100) {
        segments.push({ width: 100 - prevPct, color: lastColor });
    }

    return segments;
}

function buildMarkers(stops: ColorStop[], min: number, max: number) {
    const sorted = [...stops].sort((a, b) => a[0] - b[0]);
    const offsets = [0, ...sorted.map((s) => s[0])];

    return offsets.map((offset, index) => {
        const isEdge = index === 0 || index === offsets.length - 1;
        const rawValue = offsetToValue(offset, min, max);
        const range = max - min;
        const labelValue =
            range <= 50 ? rawValue.toFixed(2) : String(Math.round(rawValue));

        return {
            offset,
            label: labelValue,
            isEdge,
            stopIndexForDrag: isEdge ? null : index - 1,
        };
    });
}

function buildEdgeMarkers(min: number, max: number) {
    return [
        { offset: 0, label: formatMarkerValue(0, min, max) },
        { offset: 100, label: formatMarkerValue(100, min, max) },
    ];
}

const toCssColor = (color: Color) => {
    if (typeof color === "string") {
        return parseColorToHex(color);
    }

    if ("h" in color) {
        return `hsl(${color.h}, ${color.s}%, ${color.l}%)`;
    }

    return `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 1})`;
};

const moveDraggedStop = (
    stops: ColorStop[],
    stopIndex: number,
    newOffset: number
) => {
    const next = [...stops].sort((a, b) => a[0] - b[0]);
    const [, color, label] = next[stopIndex];

    const minBound = stopIndex === 0 ? 0 : next[stopIndex - 1][0] + 0.1;
    const maxBound =
        stopIndex === next.length - 1 ? 100 : next[stopIndex + 1][0] - 0.1;

    const clampedOffset = clamp(newOffset, minBound, maxBound);
    next[stopIndex] = [clampedOffset, color, label];

    return next;
};

const mapStopsForOutput = (
    stops: ColorStop[],
    offsetConverter: OffsetConverter
): ColorStop[] => {
    return stops.map(([offset, stopColor, stopLabel]) => {
        const nextOffset = offsetConverter.fromPercent(offset);
        if (typeof stopLabel === "string") {
            return [nextOffset, stopColor, stopLabel];
        }

        // Keep tuple shape clean for persistence payloads:
        // [offset, color] instead of [offset, color, undefined -> null].
        return [nextOffset, stopColor];
    });
};

const ColorRangeBar = ({
    min,
    max,
    colors,
    onChange,
    draggable = true,
    mode = "default",
}: ColorRangeBarProps) => {
    const usesDecimalOffset = useMemo(
        () =>
            Array.isArray(colors) &&
            colors.length > 0 &&
            Math.max(...colors.map(([offset]) => Number(offset) || 0)) <= 1,
        [colors]
    );

    const offsetConverter = useMemo(
        () => getOffsetConverter(usesDecimalOffset),
        [usesDecimalOffset]
    );

    const [localStops, setLocalStops] = useState<ColorStop[]>(() => {
        return sortAndNormalizeStops(colors, offsetConverter.toPercent);
    });

    useEffect(() => {
        setLocalStops(sortAndNormalizeStops(colors, offsetConverter.toPercent));
    }, [colors, offsetConverter]);

    const barRef = useRef<HTMLDivElement | null>(null);
    const [dragStopIndex, setDragStopIndex] = useState<number | null>(null);

    const segments = useMemo(() => buildSegments(localStops), [localStops]);
    const markers = useMemo(() => buildMarkers(localStops, min, max), [localStops, min, max]);
    const edgeMarkers = useMemo(() => buildEdgeMarkers(min, max), [min, max]);
    const dragMarker =
        dragStopIndex !== null ? markers[dragStopIndex + 1] ?? null : null;

    useEffect(() => {
        if (!draggable || dragStopIndex === null || !barRef.current) return;

        const handleMove = (e: MouseEvent | TouchEvent) => {
            const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;

            const rect = barRef.current!.getBoundingClientRect();
            const ratio = clamp((clientX - rect.left) / rect.width, 0, 1);

            const newOffset = ratio * 100;

            setLocalStops((prev) => {
                const next = moveDraggedStop(prev, dragStopIndex, newOffset);

                if (onChange) {
                    onChange(mapStopsForOutput(next, offsetConverter));
                }

                return next;
            });
        };

        const handleUp = () => {
            setDragStopIndex(null);
        };

        globalThis.addEventListener("mousemove", handleMove);
        globalThis.addEventListener("mouseup", handleUp);
        globalThis.addEventListener("touchmove", handleMove);
        globalThis.addEventListener("touchend", handleUp);
        globalThis.addEventListener("touchcancel", handleUp);

        return () => {
            globalThis.removeEventListener("mousemove", handleMove);
            globalThis.removeEventListener("mouseup", handleUp);
            globalThis.removeEventListener("touchmove", handleMove);
            globalThis.removeEventListener("touchend", handleUp);
            globalThis.removeEventListener("touchcancel", handleUp);
        };
    }, [dragStopIndex, draggable, offsetConverter, onChange]);

    const startDrag = (stopIndex: number | null) => {
        if (!draggable || stopIndex == null) return;
        setDragStopIndex(stopIndex);
    };

    return (
        <ColorRange mode={mode}>
            <div className="color-range__bar" ref={barRef}>
                {segments.map((seg, idx) => (
                    <div
                        key={`${seg.width}-${toCssColor(seg.color)}-${idx}`}
                        className="color-range__segment"
                        style={{
                            width: `${seg.width}%`,
                            backgroundColor: toCssColor(seg.color),
                        }}
                    />
                ))}
            </div>

            <div className="color-range__markers">
                {edgeMarkers.map((marker, idx) => (
                    <div
                        key={`edge-${marker.offset}-${marker.label}-${idx}`}
                        className="color-range__marker"
                        style={{
                            left: `${marker.offset}%`,
                            transform:
                                marker.offset === 0
                                    ? "translateX(0)"
                                    : "translateX(-100%)",
                        }}
                    >
                        <div className="color-range__marker-label">{marker.label}</div>
                    </div>
                ))}

                {draggable &&
                    markers
                        .filter((marker) => marker.stopIndexForDrag !== null)
                        .map((marker, idx) => (
                            <button
                                type="button"
                                key={`handle-${marker.offset}-${marker.stopIndexForDrag}-${idx}`}
                                className="color-range__handle"
                                style={{ left: `${marker.offset}%` }}
                                onMouseDown={() => startDrag(marker.stopIndexForDrag)}
                                onTouchStart={() => startDrag(marker.stopIndexForDrag)}
                            />
                        ))}

                {dragMarker && (
                    <div
                        className="color-range__drag-indicator"
                        style={{ left: `${dragMarker.offset}%` }}
                    >
                        <div className="color-range__drag-label">
                            {formatMarkerValue(dragMarker.offset, min, max)}
                        </div>
                    </div>
                )}
            </div>
        </ColorRange>
    );
};

export const ColorRangeEditor = ({
    min,
    max,
    colors,
    range,
    onRangeChange,
    onColorChange,
    onStopsChange,
    rangeOptions = [2, 3, 4],
    className,
    mode = "default",
}: ColorRangeEditorProps) => {
    return (
        <div className={className}>
            <div className="d-flex align-items-end">
                <Form.Group className="mr-3">
                    <Form.Control
                        className="pr-4"
                        id="color-range"
                        name="color-range"
                        as="select"
                        value={range}
                        placeholder="Range"
                        onChange={(event) =>
                            onRangeChange(Number(event.target.value))
                        }
                        custom
                    >
                        {rangeOptions.map((option) => (
                            <option key={option} value={option}>
                                {option}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                {colors.map((color, idx) => (
                    <Form.Group
                        className="mr-2"
                        key={`color-picker-${color[0]}-${color[2] ?? "stop"}`}
                    >
                        <ColorPicker
                            aria-label="color-picker"
                            defaultValue={typeof color[1] === "string" ? color[1] : toCssColor(color[1])}
                            onChange={(_event, value) => onColorChange(value, idx)}
                        />
                    </Form.Group>
                ))}
            </div>

            <div className={mode === "compact" ? "mt-2" : "mt-3"}>
                <ColorRangeBar
                    colors={colors}
                    min={min}
                    max={max}
                    mode={mode}
                    onChange={onStopsChange}
                />
            </div>
        </div>
    );
};

export default ColorRangeBar;

